import React, { useEffect, useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Building2, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';

import { DateField } from '../shared/DateField';

/**
 * NewPortcoScreen — create a portfolio company.
 *
 * <p>POSTs to {@code POST /api/portco}. The service generates the {@code PO}
 * synthetic CUSIP and an entry in {@code dim_security_master}. Funds and
 * commitments are sourced from the user's existing PE commitments so a portco
 * can be optionally tied back to its sponsoring fund + commitment.
 */

interface Portfolio {
  id: number;
  name?: string;
  portfolioName?: string;
}

interface Commitment {
  id: number;
  fundId: number;
  fundName?: string;
  portfolioId: number;
  status?: string;
}

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 32px 40px;
  color: #0f172a;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #0b1e3a;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 24px 0;
  max-width: 720px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  max-width: 920px;
`;

const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols ?? 2}, 1fr);
  gap: 16px;
  margin-bottom: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
`;

const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.15);
  }
`;

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.15);
  }
`;

const Textarea = styled.textarea`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  background: #fff;
  resize: vertical;
  min-height: 64px;

  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.15);
  }
`;

const SectionDivider = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 24px 0 12px 0;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 6px;
`;

const Submit = styled.button`
  background: #1e3a8a;
  color: #fff;
  border: 0;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: #1e40af;
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const Banner = styled.div<{ $tone: 'success' | 'error' }>`
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid ${(p) => (p.$tone === 'success' ? '#16a34a' : '#dc2626')};
  background: ${(p) => (p.$tone === 'success' ? '#f0fdf4' : '#fef2f2')};
  color: ${(p) => (p.$tone === 'success' ? '#166534' : '#991b1b')};
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const today = () => getTodayDate();

const NewPortcoScreen: React.FC = () => {
  const navigate = useNavigate();

  // Identity
  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [companyTicker, setCompanyTicker] = useState('');
  const [country, setCountry] = useState('USA');
  const [headquartersCity, setHeadquartersCity] = useState('');
  const [sector, setSector] = useState('');
  const [industry, setIndustry] = useState('');

  // Linkage to fund / commitment / portfolio
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [commitmentId, setCommitmentId] = useState<string>('');

  // Investment details
  const [investmentType, setInvestmentType] = useState('BUYOUT');
  const [dealRole, setDealRole] = useState('SPONSOR');
  const [ownershipPct, setOwnershipPct] = useState('');
  const [leadPartner, setLeadPartner] = useState('');
  const [initialInvestmentDate, setInitialInvestmentDate] = useState(today());
  const [initialInvestedAmount, setInitialInvestedAmount] = useState('');
  const [initialEnterpriseValue, setInitialEnterpriseValue] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<
    { tone: 'success' | 'error'; message: string; portcoId?: number } | null
  >(null);

  // Load portfolios on mount.
  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(
      (r) => {
        const data = r.data?.content || r.data || [];
        setPortfolios(Array.isArray(data) ? data : []);
      },
      () => setPortfolios([]),
    );
  }, []);

  // When portfolio changes, load that portfolio's commitments so the user can
  // optionally tie this portco back to a specific sponsoring fund + commitment.
  useEffect(() => {
    if (!portfolioId) {
      setCommitments([]);
      setCommitmentId('');
      return;
    }
    axios.get<Commitment[]>(`/api/alternatives/commitments/portfolio/${portfolioId}`).then(
      (r) => setCommitments(Array.isArray(r.data) ? r.data : []),
      () => setCommitments([]),
    );
  }, [portfolioId]);

  const selectedCommitment = useMemo(
    () => commitments.find((c) => String(c.id) === commitmentId),
    [commitments, commitmentId],
  );

  const canSubmit = !submitting && companyName.trim().length > 0 && currency.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);

    const body: Record<string, unknown> = {
      companyName: companyName.trim(),
      currency,
      status: 'ACTIVE',
    };
    if (legalName.trim()) body.legalName = legalName.trim();
    if (companyTicker.trim()) body.companyTicker = companyTicker.trim().toUpperCase();
    if (country.trim()) body.country = country.trim().toUpperCase().slice(0, 3);
    if (headquartersCity.trim()) body.headquartersCity = headquartersCity.trim();
    if (sector.trim()) body.sector = sector.trim();
    if (industry.trim()) body.industry = industry.trim();
    if (investmentType) body.investmentType = investmentType;
    if (dealRole) body.dealRole = dealRole;
    if (ownershipPct) body.ownershipPct = Number(ownershipPct);
    if (leadPartner.trim()) body.leadPartner = leadPartner.trim();
    if (initialInvestmentDate) body.initialInvestmentDate = initialInvestmentDate;
    if (initialInvestedAmount) body.initialInvestedAmount = Number(initialInvestedAmount);
    if (initialEnterpriseValue) body.initialEnterpriseValue = Number(initialEnterpriseValue);
    if (costBasis) body.costBasis = Number(costBasis);
    if (portfolioId) body.portfolioId = Number(portfolioId);
    if (selectedCommitment) {
      body.commitmentId = selectedCommitment.id;
      body.fundId = selectedCommitment.fundId;
    }
    if (notes.trim()) body.notes = notes.trim();

    try {
      const r = await axios.post('/api/portco', body);
      const cusip = r.data?.syntheticCusip || '—';
      const id = r.data?.id;
      setResult({
        tone: 'success',
        portcoId: id,
        message: `Portfolio company created. Synthetic CUSIP ${cusip}. Master record persisted.`,
      });
    } catch (e: any) {
      setResult({
        tone: 'error',
        message:
          'Create failed: ' +
          (e?.response?.data?.message || e?.message || 'unknown error'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/portco')}>
          <ArrowLeft size={14} />
          Back to portfolio companies
        </BackBtn>
      </TopBar>

      <Title>
        <Building2 size={22} />
        New portfolio company
      </Title>
      <Subtitle>
        Create a master record for a portfolio company. The company gets a 9-character{' '}
        <code style={{ background: '#eef2ff', padding: '1px 4px' }}>PO</code> synthetic CUSIP and an
        entry in <code style={{ background: '#eef2ff', padding: '1px 4px' }}>dim_security_master</code>{' '}
        so it can be referenced by KPIs, valuations, sponsor-finance debt, and covenants.
      </Subtitle>

      <Card>
        <SectionDivider>Identity</SectionDivider>
        <Grid $cols={2}>
          <Field>
            <Label>Company name *</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Apex Industrial Holdings"
            />
          </Field>
          <Field>
            <Label>Legal name</Label>
            <Input
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="e.g. Apex Industrial Holdings, LLC"
            />
          </Field>
        </Grid>
        <Grid $cols={3}>
          <Field>
            <Label>Ticker (if any)</Label>
            <Input
              value={companyTicker}
              onChange={(e) => setCompanyTicker(e.target.value)}
              placeholder="optional"
            />
          </Field>
          <Field>
            <Label>Country (ISO)</Label>
            <Input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="USA"
              maxLength={3}
            />
          </Field>
          <Field>
            <Label>Headquarters city</Label>
            <Input
              value={headquartersCity}
              onChange={(e) => setHeadquartersCity(e.target.value)}
              placeholder="e.g. Chicago"
            />
          </Field>
        </Grid>
        <Grid $cols={2}>
          <Field>
            <Label>Sector</Label>
            <Input
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="e.g. Industrials"
            />
          </Field>
          <Field>
            <Label>Industry</Label>
            <Input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g. Specialty Chemicals"
            />
          </Field>
        </Grid>

        <SectionDivider>Sponsor / Fund linkage (optional)</SectionDivider>
        <Grid $cols={2}>
          <Field>
            <Label>Portfolio</Label>
            <Select value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)}>
              <option value="">— None —</option>
              {portfolios.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.portfolioName || p.name || `Portfolio #${p.id}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>PE commitment / fund</Label>
            <Select
              value={commitmentId}
              onChange={(e) => setCommitmentId(e.target.value)}
              disabled={!portfolioId || commitments.length === 0}
            >
              <option value="">— None —</option>
              {commitments.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.fundName ? `${c.fundName} (commitment #${c.id})` : `Commitment #${c.id}`}
                </option>
              ))}
            </Select>
          </Field>
        </Grid>

        <SectionDivider>Deal details</SectionDivider>
        <Grid $cols={3}>
          <Field>
            <Label>Investment type</Label>
            <Select value={investmentType} onChange={(e) => setInvestmentType(e.target.value)}>
              <option value="BUYOUT">Buyout</option>
              <option value="GROWTH">Growth</option>
              <option value="VC">Venture</option>
              <option value="MINORITY">Minority</option>
              <option value="SPECIAL_SITS">Special Situations</option>
            </Select>
          </Field>
          <Field>
            <Label>Deal role</Label>
            <Select value={dealRole} onChange={(e) => setDealRole(e.target.value)}>
              <option value="SPONSOR">Sponsor</option>
              <option value="CO_INVESTOR">Co-investor</option>
              <option value="SECONDARY">Secondary</option>
            </Select>
          </Field>
          <Field>
            <Label>Ownership %</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={ownershipPct}
              onChange={(e) => setOwnershipPct(e.target.value)}
              placeholder="e.g. 75"
            />
          </Field>
        </Grid>
        <Grid $cols={2}>
          <Field>
            <Label>Lead partner</Label>
            <Input
              value={leadPartner}
              onChange={(e) => setLeadPartner(e.target.value)}
              placeholder="e.g. J. Smith"
            />
          </Field>
          <Field>
            <Label>Initial investment date</Label>
            <DateField
              value={initialInvestmentDate ?? ''}
              onChange={iso => setInitialInvestmentDate(iso)}
            />
          </Field>
        </Grid>
        <Grid $cols={3}>
          <Field>
            <Label>Currency</Label>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
              <option value="CAD">CAD</option>
            </Select>
          </Field>
          <Field>
            <Label>Initial invested amount</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={initialInvestedAmount}
              onChange={(e) => setInitialInvestedAmount(e.target.value)}
              placeholder="e.g. 250000000"
            />
          </Field>
          <Field>
            <Label>Initial enterprise value</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={initialEnterpriseValue}
              onChange={(e) => setInitialEnterpriseValue(e.target.value)}
              placeholder="e.g. 850000000"
            />
          </Field>
        </Grid>
        <Grid $cols={1}>
          <Field>
            <Label>Cost basis</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={costBasis}
              onChange={(e) => setCostBasis(e.target.value)}
              placeholder="defaults to initial invested amount"
            />
          </Field>
        </Grid>

        <SectionDivider>Notes</SectionDivider>
        <Grid $cols={1}>
          <Field>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Thesis, key risks, board structure, etc."
            />
          </Field>
        </Grid>

        <Submit disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? 'Creating…' : 'Create portfolio company'}
        </Submit>

        {result && (
          <Banner $tone={result.tone}>
            {result.tone === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{result.message}</span>
            {result.tone === 'success' && result.portcoId != null && (
              <button
                onClick={() => navigate(`/portco/${result.portcoId}`)}
                style={{
                  marginLeft: 'auto',
                  background: '#0b1e3a',
                  color: '#fff',
                  border: 0,
                  padding: '4px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                Open detail →
              </button>
            )}
          </Banner>
        )}
      </Card>
    </Page>
  );
};

export default NewPortcoScreen;
